import { clsx } from 'clsx'

import { TagIcon } from '@buffer-mono/popcorn'

import { SERVICE_GOOGLEBUSINESS } from '~publish/legacy/constants'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import {
  getFormattedDateAndTime,
  parseGoogleBusinessLinks,
} from '~publish/legacy/utils/channels/googlebusiness/helpers'

import {
  getPreviewCharLimit,
  getPreviewTitleCharLimit,
  textExceedsCharLimit,
  truncateText,
} from '../../../Common/utils'
import type { GoogleBusinessContent } from '../../../types'
import { ImagePreview } from '../ImagePreview'

import styles from './PostBody.module.css'

import type { JSX } from 'react'

const maxLength = getPreviewCharLimit(SERVICE_GOOGLEBUSINESS)
const maxLengthTitleAndCode = getPreviewTitleCharLimit(SERVICE_GOOGLEBUSINESS)

const getTextToDisplay = ({
  length,
  text,
}: {
  length: number
  text?: string
}): string | undefined => {
  let textToDisplay = text

  textToDisplay = truncateText(textToDisplay, length)

  return textToDisplay
}

const PostBody = ({
  content,
}: {
  content: GoogleBusinessContent
}): JSX.Element => {
  const { channelData, images, text, updateType } = content
  const googlebusiness = channelData?.googlebusiness

  const textToDisplay = getTextToDisplay({ length: maxLength, text })
  const titleToDisplay = getTextToDisplay({
    length: maxLengthTitleAndCode,
    text: googlebusiness?.title || '',
  })
  const codeToDisplay = getTextToDisplay({
    length: maxLengthTitleAndCode,
    text: googlebusiness?.code || '',
  })

  const isWhatsNew = updateType === 'whats_new'
  const isEvent = updateType === 'event'
  const isOffer = updateType === 'offer'

  const shouldShowImage = images && images.length > 0
  const shouldShowTitleAndDate = (isEvent || isOffer) && titleToDisplay
  const shouldShowDate = googlebusiness?.start_date && googlebusiness?.end_date
  const shouldShowOfferLink =
    isOffer && googlebusiness?.link && googlebusiness?.terms

  const image = images && images[0]

  return (
    <div className={styles.body}>
      {shouldShowImage && <ImagePreview image={image} />}
      <div
        className={clsx(
          styles.content,
          shouldShowImage && styles.hasImage,
          isWhatsNew && styles.whatsNew,
        )}
      >
        {shouldShowTitleAndDate && (
          <>
            <div className={styles.titleWrapper}>
              <div
                className={clsx(styles.title, isOffer && styles.offer)}
                data-testid="title"
                translate="no"
              >
                {titleToDisplay}
              </div>
              {isOffer && (
                <TagIcon
                  className={styles.tag}
                  size="large"
                  data-testid="offer-tag"
                />
              )}
            </div>
            {shouldShowDate && (
              <div className={styles.date}>
                {getFormattedDateAndTime({
                  startDate: googlebusiness?.start_date,
                  endDate: googlebusiness?.end_date,
                  startTime: googlebusiness?.start_time,
                  endTime: googlebusiness?.end_time,
                })}
              </div>
            )}
          </>
        )}
        <LinkifiedText
          className={styles.linkifiedText}
          links={parseGoogleBusinessLinks(textToDisplay)}
          size="mini"
          whitespace="pre-wrap"
          newTab
        >
          {textToDisplay}
        </LinkifiedText>
        {text && textExceedsCharLimit(text, maxLength) && (
          <div className={styles.more}>... More</div>
        )}
        {isOffer && googlebusiness?.code && (
          <div className={styles.offerDetails}>
            <div className={styles.offerDirections}>
              Show this code at the store
            </div>
            <div
              className={styles.offerCode}
              data-testid="offer-code"
              translate="no"
            >
              {codeToDisplay}
            </div>
            <div className={styles.offerDates}>
              Valid{' '}
              {getFormattedDateAndTime({
                startDate: googlebusiness?.start_date,
                endDate: googlebusiness?.end_date,
                startTime: googlebusiness?.start_time,
                endTime: googlebusiness?.end_time,
                format: 'M/D/YYYY',
              })}
            </div>
          </div>
        )}
        {shouldShowOfferLink && (
          <div className={styles.offerLink}>Redeem offer</div>
        )}
      </div>
    </div>
  )
}

export default PostBody
